import { useCallback, useId, useState } from 'react';
import BottomDrawer from '../../../_custom/BottomDrawer/BottomDrawer';
import Tooltip from '../../../_custom/Tooltip/Tooltip';
import { useIsMobile } from '../../../_custom/Hooks/useIsMobile';
import { MORE_VALUES_HIDE_DELAY_MS, MORE_VALUES_SHEET_HEADING } from '../../constants';
import { attributeValues, displayValueFor, indexOfDisplayedValue, maskedHintFor, primaryIndexOf } from '../../utils/allKnownDetailsUtils';
import type { AttributedKnownValue } from '../../utils/allKnownDetailsUtils';
import type { KnownDetailType, KnownEntityType, KnownSourceGroup } from '../../types';
import MoreValuesTrigger from '../MoreValuesTrigger/MoreValuesTrigger';
import styles from './MoreValuesPopover.module.scss';

interface MoreValuesPopoverProps {
    groups?: KnownSourceGroup[];
    type: KnownDetailType;
    entityType: KnownEntityType;
    noun?: string;
    displayedValue?: string;
    triggerClassName?: string;
}

const OverflowRows = ({ overflow, type }: { overflow: AttributedKnownValue[]; type: KnownDetailType }) => (
    <>
        {overflow.map((entry, index) => (
            <div className={styles.OverflowRow} key={`${entry.source}-${entry.value}-${index}`}>
                <span className={styles.OverflowSource}>{entry.source}</span>
                <span className={styles.OverflowDot} aria-hidden="true">
                    ·
                </span>
                <span className={styles.OverflowValue} title={maskedHintFor(entry.value, type)}>
                    {displayValueFor(entry.value, type)}
                </span>
            </div>
        ))}
    </>
);

/** Hover tooltip on desktop, bottom sheet on touch (a second SideDrawer can't nest). */
const MoreValuesPopoverBody = ({
    overflow,
    type,
    noun,
    triggerClassName,
}: {
    overflow: AttributedKnownValue[];
    type: KnownDetailType;
    noun?: string;
    triggerClassName?: string;
}) => {
    const isMobile = useIsMobile();
    const [sheetOpen, setSheetOpen] = useState(false);
    const tooltipId = useId();
    // Reference the tooltip id only while it is mounted.
    const [tooltipShown, setTooltipShown] = useState(false);
    const trackTooltip = useCallback((node: HTMLDivElement | null) => setTooltipShown(!!node), []);

    return (
        <span className={styles.Popover}>
            {isMobile ? (
                <>
                    <MoreValuesTrigger
                        count={overflow.length}
                        noun={noun}
                        onActivate={() => setSheetOpen(true)}
                        className={triggerClassName}
                    />
                    {sheetOpen && (
                        <BottomDrawer
                            onClose={() => setSheetOpen(false)}
                            height="auto"
                            showHeading
                            heading={MORE_VALUES_SHEET_HEADING[type]}
                            dialogLabel={MORE_VALUES_SHEET_HEADING[type]}
                        >
                            <div className={styles.SheetList}>
                                <OverflowRows overflow={overflow} type={type} />
                            </div>
                        </BottomDrawer>
                    )}
                </>
            ) : (
                <Tooltip
                    content={
                        <div className={styles.OverflowList} role="tooltip" id={tooltipId} ref={trackTooltip}>
                            <OverflowRows overflow={overflow} type={type} />
                        </div>
                    }
                    direction="bottom"
                    small
                    fitContent
                    usePortal
                    hideDelay={MORE_VALUES_HIDE_DELAY_MS}
                >
                    <MoreValuesTrigger
                        count={overflow.length}
                        noun={noun}
                        describedBy={tooltipShown ? tooltipId : undefined}
                        className={triggerClassName}
                    />
                </Tooltip>
            )}
        </span>
    );
};

const MoreValuesPopover = ({ groups, type, entityType, noun, displayedValue, triggerClassName }: MoreValuesPopoverProps) => {
    const attributed = attributeValues(groups, entityType);
    // No displayed value passed means the surface shows the primary; otherwise drop what it shows.
    const shownIndex = displayedValue === undefined ? primaryIndexOf(attributed) : indexOfDisplayedValue(attributed, displayedValue, type);
    const overflow = attributed.filter((_, index) => index !== shownIndex);
    if (!overflow.length) return null;

    return <MoreValuesPopoverBody overflow={overflow} type={type} noun={noun} triggerClassName={triggerClassName} />;
};

export default MoreValuesPopover;
